import { uuid } from './uuid.ts';
/**
 * File d'attente hors-ligne (#14) des messages du chat. Stockée en localStorage
 * sous forme d'OBJETS {id, texte} (survivent au rechargement). Sémantique clé :
 * un message n'est retiré qu'APRÈS son traitement (retirerDeFile, appelé sur
 * succès ou erreur serveur définitive) — un rechargement PENDANT le rejeu ne perd
 * donc plus rien, contrairement à l'ancien « vider la file avant d'envoyer ».
 * Isolé de App.tsx pour être testable sans monter le composant.
 */
export interface FileItem {
  id: string;
  texte: string;
  /** Mise en file (ms epoch). Purge RGPD : un message abandonné ne reste pas
   *  indéfiniment en clair sur l'appareil. Absent = entrée legacy (conservée). */
  ts?: number;
}

const CLE_FILE = 'ocerm_file_attente';
/** Au-delà, un message en file est considéré abandonné et purgé (RGPD, §9.2). */
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

export function lireFile(): FileItem[] {
  try {
    const v = JSON.parse(localStorage.getItem(CLE_FILE) ?? '[]');
    if (!Array.isArray(v)) return [];
    const maintenant = Date.now();
    // On ignore toute entrée malformée (ancien format string[], données corrompues)
    // ET tout message abandonné au-delà du TTL (entrées legacy sans ts conservées).
    const valides = v.filter(
      (x): x is FileItem =>
        typeof x?.id === 'string' &&
        typeof x?.texte === 'string' &&
        (typeof x?.ts !== 'number' || maintenant - x.ts < TTL_MS),
    );
    // Purge EFFECTIVE (pas un simple filtre d'affichage) : si des entrées expirées/
    // malformées ont été retirées, on réécrit la file épurée -> la PII ne reste pas en
    // clair sur l'appareil au-delà du TTL, même si rien d'autre ne re-déclenche d'écriture.
    if (valides.length !== v.length) ecrireFile(valides);
    return valides;
  } catch {
    return [];
  }
}

export function ecrireFile(f: FileItem[]): void {
  // Stockage indisponible (navigation privée iOS, quota plein) : on n'échoue pas
  // l'envoi pour autant ; au pire la file ne survit pas au rechargement.
  try {
    localStorage.setItem(CLE_FILE, JSON.stringify(f));
  } catch {
    /* ignore : pas de persistance hors-ligne disponible */
  }
}

export function mettreEnFile(texte: string): void {
  ecrireFile([...lireFile(), { id: uuid(), texte, ts: Date.now() }]);
}

export function retirerDeFile(itemId: string): void {
  ecrireFile(lireFile().filter((x) => x.id !== itemId));
}
